import { useState } from "react";
import { toast } from "sonner";
import { Check, Camera } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { PageHero, CtaBand, SectionHead, Field, Input, Select, Textarea, SubmitButton, usePageMeta } from "@/components/shared";
import GuestCleaning from "@/components/GuestCleaning";
import CleaningPricing from "@/components/CleaningPricing";
import HostPartnerForm from "@/components/HostPartnerForm";
import { submitLead } from "@/lib/api";

const CHECKLIST = [
  "Check-out cleaning",
  "Kitchen cleaning & detailing",
  "Bathroom sanitizing",
  "Beds made with fresh linens",
  "Fresh towel sets",
  "Laundry coordination",
  "Trash removal",
  "Amenity restocking",
  "Inventory check",
  "Final property-ready inspection",
  "Optional before & after photographs",
];

const initial = {
  property_location: "", bedrooms: "", bathrooms: "", beds: "",
  laundry_required: "", monthly_turnovers: "", listing_url: "",
  contact_name: "", phone: "", email: "",
};

export default function Cleaning() {
  usePageMeta("Vacation Rental Cleaning", "Hotel-grade vacation rental turnover cleaning in Bermuda — linens, restocking, inventory checks and property-ready inspections. Get a cleaning quote.");
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead("cleaning_quote", { ...form, name: form.contact_name });
      setDone(true);
      toast.success("Quote request received — we'll respond shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <PageHero
        overline="Vacation Rental Cleaning"
        lines={["Five-Star Turnovers,", "Every Single Time."]}
        copy="A turnover is not a house clean. It is a documented, inspected reset to a flawless guest-ready standard — linens, amenities, inventory and a final walk-through before the next arrival."
        image={IMAGES.bathroom}
      >
        <a href="#cleaning-quote" data-testid="cleaning-hero-quote-btn" className="inline-flex items-center gap-2.5 bg-bermuda px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-ivory">
          Get a Cleaning Quote
        </a>
      </PageHero>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          <div>
            <SectionHead
              overline="The Turnover Standard"
              title="A written checklist, verified before every arrival."
              copy="Every turnover follows the same documented protocol — because consistency, not luck, is what earns five-star cleanliness reviews."
            />
            <ul className="mt-10 space-y-0">
              {CHECKLIST.map((item, i) => (
                <Reveal key={item} delay={i * 0.04}>
                  <li className="flex items-center gap-4 border-t border-navy/10 py-4 last:border-b">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ocean-light text-ocean">
                      <Check size={12} strokeWidth={2.5} />
                    </span>
                    <span className="text-sm text-navy/80">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.2}>
              <div className="mt-8 flex items-start gap-4 bg-sand-light p-6">
                <Camera size={18} className="mt-0.5 shrink-0 text-bermuda-dark" />
                <p className="text-sm leading-relaxed text-navy/70">
                  Optional before-and-after and property-ready photographs give overseas owners visible proof that their home is perfect — and document its condition at every changeover.
                </p>
              </div>
            </Reveal>
          </div>

          <div id="cleaning-quote" className="scroll-mt-28">
            <Reveal>
              <div className="border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-10">
                {done ? (
                  <div data-testid="cleaning-quote-success" className="py-10 text-center">
                    <h3 className="font-serif text-3xl text-navy">Thank you.</h3>
                    <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-navy/60">
                      Your cleaning quote request has been received. Our local team will respond with a tailored quote shortly.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="font-serif text-2xl text-navy">Get a Cleaning Quote</h3>
                    <p className="mt-2 text-sm text-navy/55">Tell us about your property — quotes are tailored to size, linens and turnover frequency.</p>
                    <form onSubmit={onSubmit} className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="cleaning-quote-form">
                      <Field label="Property Location / Parish" required><Input data-testid="quote-location" required value={form.property_location} onChange={set("property_location")} placeholder="e.g. Warwick" /></Field>
                      <Field label="Number of Bedrooms" required><Input data-testid="quote-bedrooms" required type="number" min="0" value={form.bedrooms} onChange={set("bedrooms")} /></Field>
                      <Field label="Number of Bathrooms" required><Input data-testid="quote-bathrooms" required type="number" min="0" step="0.5" value={form.bathrooms} onChange={set("bathrooms")} /></Field>
                      <Field label="Number of Beds" required><Input data-testid="quote-beds" required type="number" min="0" value={form.beds} onChange={set("beds")} /></Field>
                      <Field label="Laundry Required?" required>
                        <Select data-testid="quote-laundry" required value={form.laundry_required} onChange={set("laundry_required")}>
                          <option value="">Select…</option><option>Yes — on-site</option><option>Yes — off-site / linen service</option><option>No</option>
                        </Select>
                      </Field>
                      <Field label="Avg. Monthly Turnovers" required><Input data-testid="quote-turnovers" required type="number" min="0" value={form.monthly_turnovers} onChange={set("monthly_turnovers")} /></Field>
                      <div className="sm:col-span-2"><Field label="Airbnb / VRBO Listing URL"><Input data-testid="quote-listing-url" value={form.listing_url} onChange={set("listing_url")} placeholder="https://" /></Field></div>
                      <Field label="Contact Name" required><Input data-testid="quote-name" required value={form.contact_name} onChange={set("contact_name")} /></Field>
                      <Field label="Phone" required><Input data-testid="quote-phone" required type="tel" value={form.phone} onChange={set("phone")} /></Field>
                      <div className="sm:col-span-2"><Field label="Email" required><Input data-testid="quote-email" required type="email" value={form.email} onChange={set("email")} /></Field></div>
                      <div className="sm:col-span-2">
                        <SubmitButton loading={loading} testid="cleaning-quote-submit">Get My Cleaning Quote</SubmitButton>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CleaningPricing />

      <HostPartnerForm />

      <GuestCleaning />

      <CtaBand title="Prefer full management, cleaning included?" copy="Cleaning coordination and quality verification are built into our Full-Service and White-Glove management plans." />
    </main>
  );
}
